import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from '@kostbar/i18n'
import { toast } from 'sonner'
import { Info, Loader2, Trash2 } from 'lucide-react'
import { useAssignments } from '../hooks/use-assignments'
import { useRestaurants } from '../hooks/use-restaurants'
import { useProductDetail } from '../hooks/use-product-detail'
import { productsApi } from '@/api/endpoints/products'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { ResponsiveTableWrapper } from '@/shared/components/responsive-table-wrapper'
import { formatCurrency } from '@/shared/utils/format-currency'

interface VolumePricingTabProps {
  sku: string
}

export function VolumePricingTab({ sku }: VolumePricingTabProps) {
  const { t } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()
  const [editingRestaurantId, setEditingRestaurantId] = useState<number | null>(
    null,
  )
  const [editMinQty, setEditMinQty] = useState('')
  const [editPrice, setEditPrice] = useState('')

  const { data: product } = useProductDetail(sku)
  // A merged duplicate (CHG-2026-09-duplicate-product-merge) is not independently sellable, so
  // its volume pricing is frozen — configure the primary product instead.
  const isMerged = !!product?.mergedIntoSku

  const { data: tiers, isLoading: tiersLoading } = useQuery({
    queryKey: ['products', sku, 'volume-tiers'],
    queryFn: () => productsApi.listVolumeTiers(sku),
    enabled: !!sku,
  })

  const { data: assignments, isLoading: assignmentsLoading } =
    useAssignments(sku)
  const { data: restaurants, isLoading: restaurantsLoading } = useRestaurants()

  const saveMutation = useMutation({
    mutationFn: ({
      restaurantId,
      minQty,
      amountCents,
    }: {
      restaurantId: number
      minQty: number
      amountCents: number
    }) => productsApi.setVolumeTier(sku, restaurantId, { minQty, amountCents }),
    onSuccess: () => {
      toast.success(t('volumePricing.saveSuccess'))
      queryClient.invalidateQueries({
        queryKey: ['products', sku, 'volume-tiers'],
      })
      setEditingRestaurantId(null)
    },
    onError: () => {
      toast.error(t('volumePricing.saveFailed'))
    },
  })

  const removeMutation = useMutation({
    mutationFn: (restaurantId: number) =>
      productsApi.deleteVolumeTier(sku, restaurantId),
    onSuccess: () => {
      toast.success(t('volumePricing.removeSuccess'))
      queryClient.invalidateQueries({
        queryKey: ['products', sku, 'volume-tiers'],
      })
    },
    onError: () => {
      toast.error(t('volumePricing.removeFailed'))
    },
  })

  function startEditing(
    restaurantId: number,
    currentMinQty?: number,
    currentAmountCents?: number,
  ) {
    setEditingRestaurantId(restaurantId)
    setEditMinQty(currentMinQty ? String(currentMinQty) : '')
    setEditPrice(
      currentAmountCents !== undefined
        ? (currentAmountCents / 100).toFixed(2)
        : '',
    )
  }

  function handleSave(restaurantId: number) {
    const qty = parseInt(editMinQty, 10)
    if (isNaN(qty) || qty < 2) {
      toast.error(t('volumePricing.validationMinQty'))
      return
    }
    const price = parseFloat(editPrice)
    if (isNaN(price) || price < 0) {
      toast.error(t('volumePricing.validationPrice'))
      return
    }
    saveMutation.mutate({
      restaurantId,
      minQty: qty,
      amountCents: Math.round(price * 100),
    })
  }

  function handleCancel() {
    setEditingRestaurantId(null)
    setEditMinQty('')
    setEditPrice('')
  }

  function handleKeyPress(e: React.KeyboardEvent, restaurantId: number) {
    if (e.key === 'Enter') {
      handleSave(restaurantId)
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  if (tiersLoading || assignmentsLoading || restaurantsLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    )
  }

  if ((restaurants?.items.length ?? 0) === 0) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>{t('prices.noRestaurantsTitle')}</AlertTitle>
        <AlertDescription>
          {t('prices.noRestaurantsDescription')}
        </AlertDescription>
      </Alert>
    )
  }

  const assignedRestaurantIds = new Set(
    assignments?.map((a) => a.restaurantId) || [],
  )
  const assignedRestaurants =
    restaurants?.items.filter((r) => assignedRestaurantIds.has(r.id)) ?? []

  if (assignedRestaurants.length === 0) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>{t('prices.notAssignedTitle')}</AlertTitle>
        <AlertDescription>
          {t('prices.notAssignedDescription')}
        </AlertDescription>
      </Alert>
    )
  }

  const tierMap = new Map(
    tiers?.items.map((tier) => [tier.restaurantId, tier]) || [],
  )

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        {t('volumePricing.description')}
      </p>

      <ResponsiveTableWrapper>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('prices.tableRestaurant')}</TableHead>
              <TableHead>{t('prices.tableCode')}</TableHead>
              <TableHead className="text-right">
                {t('volumePricing.minQtyLabel')}
              </TableHead>
              <TableHead className="text-right">
                {t('volumePricing.discountedPriceLabel')}
              </TableHead>
              <TableHead className="text-right">
                {t('prices.tableActions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assignedRestaurants.map((restaurant) => {
              const tier = tierMap.get(restaurant.id)
              const isEditing = editingRestaurantId === restaurant.id

              return (
                <TableRow key={restaurant.id}>
                  <TableCell className="font-medium">
                    {restaurant.name}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {restaurant.code}
                  </TableCell>
                  <TableCell className="text-right">
                    {isEditing ? (
                      <Input
                        type="number"
                        min={2}
                        step={1}
                        value={editMinQty}
                        onChange={(e) => setEditMinQty(e.target.value)}
                        onKeyDown={(e) => handleKeyPress(e, restaurant.id)}
                        className="w-24 ml-auto"
                        placeholder={t('volumePricing.minQtyPlaceholder')}
                        autoFocus
                      />
                    ) : tier ? (
                      <span className="font-mono">{tier.minQty}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {isEditing ? (
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        value={editPrice}
                        onChange={(e) => setEditPrice(e.target.value)}
                        onKeyDown={(e) => handleKeyPress(e, restaurant.id)}
                        className="w-32 ml-auto"
                        placeholder={t(
                          'volumePricing.discountedPricePlaceholder',
                        )}
                      />
                    ) : tier ? (
                      <span className="font-mono">
                        {formatCurrency(tier.amountCents)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {isEditing ? (
                      <div className="flex justify-end space-x-2">
                        <Button
                          size="sm"
                          onClick={() => handleSave(restaurant.id)}
                          disabled={saveMutation.isPending}
                        >
                          {saveMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            tCommon('save')
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancel}
                          disabled={saveMutation.isPending}
                        >
                          {tCommon('cancel')}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex justify-end space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isMerged}
                          onClick={() =>
                            startEditing(
                              restaurant.id,
                              tier?.minQty,
                              tier?.amountCents,
                            )
                          }
                        >
                          {tier ? tCommon('edit') : t('volumePricing.add')}
                        </Button>
                        {tier && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => removeMutation.mutate(restaurant.id)}
                            disabled={removeMutation.isPending || isMerged}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </ResponsiveTableWrapper>
    </div>
  )
}
